import type React from 'react';

import { DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useAtomValue } from 'jotai';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { drawGeneratedAtomFamily } from 'atoms/events';
import StandardButton from 'components/buttons/StandardButton';
import TMXDialog from 'components/dialogs/TMXDialog';
import { BodyLarge, H3 } from 'components/typography/Typography';
import { useRankingsAndRatingsContext } from 'hooks/rankingsAndRatings/useRankingsAndRatingsContext';

import { RatingTypeEnum } from './types';

interface RatingsDialogProps {
  open: boolean;
  closeDialog: () => void;
  updatedAt: any;
  publishedAt: any;
  ratingType: RatingTypeEnum;
}

const RatingsDialog: React.FC<React.PropsWithChildren<RatingsDialogProps>> = ({
  open,
  closeDialog,
  updatedAt,
  publishedAt,
  ratingType,
}) => {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();
  const drawGenerated = useAtomValue(drawGeneratedAtomFamily(eventId));
  const { getRankingsAndRatings, getWtnRating, getPtPoints } = useRankingsAndRatingsContext();
  const paperProps = { style: { padding: 30, minWidth: 580 } };
  const whatUpdated = updatedAt.ratingsDialogAccessDate;
  const whatPublished = publishedAt.ratingsDialogPubDate;
  const contentKey =
    ratingType === RatingTypeEnum.NTRP
      ? 'ratingUpdateContent'
      : ratingType === RatingTypeEnum.WTN
        ? 'wtnUpdateContent'
        : 'ptUpdateContent';
  return (
    <TMXDialog open={open} PaperProps={paperProps} onClose={closeDialog}>
      <DialogTitle id="rankings-and-ratings-dialog-title">
        <Grid container justifyContent="space-between">
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <H3>
              {ratingType === RatingTypeEnum.NTRP
                ? t('ntrpRating')
                : ratingType === RatingTypeEnum.WTN
                  ? t('wtnRating')
                  : t('updatePtPoints')}
            </H3>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Trans
          components={{
            desc: <BodyLarge />,
          }}
          i18nKey={contentKey}
          style={{ marginLeft: '10px' }}
          values={{ updatedAt: whatUpdated, publishedAt: whatPublished }}
        />
      </DialogContent>
      <DialogActions>
        <StandardButton onClick={closeDialog}>{RatingTypeEnum.PT ? t('cancel') : t('continueText')}</StandardButton>
        <StandardButton
          disabled={drawGenerated}
          size="small"
          variant="primary"
          onClick={
            ratingType === RatingTypeEnum.NTRP
              ? getRankingsAndRatings
              : ratingType === RatingTypeEnum.WTN
                ? getWtnRating
                : getPtPoints
          }
        >
          {RatingTypeEnum.PT ? t('applyUpdate') : t('updateList')}
        </StandardButton>
      </DialogActions>
    </TMXDialog>
  );
};

export default RatingsDialog;
